import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FormattedDate } from '@repo/design-system-kit';
import { usePdfPreviewContext } from '../../pdf-preview-context.tsx';
import type { Document, RelatedInvoice, RelatedQuote } from '../../types';
import { ORIGIN } from '../../../constants/pdf-preview.ts';
import { DocumentDetailsPlaceholder, NumberDetailsPlaceholder } from '../../placeholders';
import { Detail, Details } from './details.tsx';

interface DocumentDetailsProps {
  document: Document;
  relatedQuote?: RelatedQuote;
  relatedInvoice?: RelatedInvoice;
}

export function DocumentDetails({
  document,
  relatedInvoice,
  relatedQuote,
}: DocumentDetailsProps): ReactNode {
  const { type } = usePdfPreviewContext();

  if (type === 'credit-note') {
    return <CreditNoteDetails document={document} relatedInvoice={relatedInvoice} />;
  }

  if (type === 'quote') {
    return <QuoteDetails document={document} />;
  }

  if (type === 'self-billing-invoice') {
    return <SelfBillingDetails document={document} relatedInvoice={relatedInvoice} />;
  }

  return <InvoiceDetails document={document} relatedQuote={relatedQuote} />;
}

function CreditNoteDetails({ document, relatedInvoice }: DocumentDetailsProps): ReactNode {
  const { formatMessage } = useIntl();
  const { displayLanguage } = usePdfPreviewContext();

  return (
    <div>
      <NumberDetailsPlaceholder>
        <Details>
          <Detail
            data-testid="pdf-preview-number"
            detailType="number"
            label={formatMessage(
              { id: 'pdf-preview.credit-note.credit-note-number' },
              { language: displayLanguage }
            )}
            value={document.number}
          />
        </Details>
      </NumberDetailsPlaceholder>

      <DocumentDetailsPlaceholder>
        <Details>
          <Detail
            data-testid="pdf-preview-issue-date"
            detailType="document"
            label={formatMessage(
              { id: 'pdf-preview.credit-note.invoice-issue-date' },
              { language: displayLanguage }
            )}
            value={<FormattedDate date={document.issueDate} locale={displayLanguage} />}
          />

          {relatedInvoice ? (
            <>
              <Detail
                data-testid="pdf-preview-related-invoice-number"
                detailType="document"
                label={formatMessage(
                  { id: 'pdf-preview.credit-note.related-invoice-number' },
                  { language: displayLanguage }
                )}
                value={relatedInvoice.number}
              />

              <Detail
                data-testid="pdf-preview-related-invoice-issue-date"
                detailType="document"
                label={formatMessage(
                  { id: 'pdf-preview.credit-note.invoice-issue-date' },
                  { language: displayLanguage }
                )}
                value={<FormattedDate date={relatedInvoice.issueDate} locale={displayLanguage} />}
              />

              {relatedInvoice.purchaseOrder ? (
                <Detail
                  data-testid="pdf-preview-related-invoice-purchase-order"
                  detailType="document"
                  label={formatMessage(
                    { id: 'pdf-preview.purchase-order' },
                    { language: displayLanguage }
                  )}
                  value={relatedInvoice.purchaseOrder}
                />
              ) : null}

              {relatedInvoice.depositInvoice ? (
                <>
                  {relatedInvoice.depositInvoice.number ? (
                    <Detail
                      data-testid="pdf-preview-related-invoice-deposit-number"
                      detailType="document"
                      label={formatMessage(
                        { id: 'pdf-preview.deposit-invoice-related-number' },
                        { language: displayLanguage }
                      )}
                      value={relatedInvoice.depositInvoice.number}
                    />
                  ) : null}
                  {relatedInvoice.depositInvoice.issueDate ? (
                    <Detail
                      data-testid="pdf-preview-related-invoice-deposit-issue-date"
                      detailType="document"
                      label={formatMessage(
                        { id: 'pdf-preview.credit-note.credit-note-issue-date' },
                        { language: displayLanguage }
                      )}
                      value={
                        <FormattedDate
                          date={relatedInvoice.depositInvoice.issueDate}
                          locale={displayLanguage}
                        />
                      }
                    />
                  ) : null}
                  {relatedInvoice.depositInvoice.paidAt ? (
                    <Detail
                      data-testid="pdf-preview-related-invoice-deposit-payment-date"
                      detailType="document"
                      label={formatMessage(
                        { id: 'pdf-preview.deposit-payment-date' },
                        { language: displayLanguage }
                      )}
                      value={
                        <FormattedDate
                          date={relatedInvoice.depositInvoice.paidAt}
                          locale={displayLanguage}
                        />
                      }
                    />
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
        </Details>
      </DocumentDetailsPlaceholder>
    </div>
  );
}

function QuoteDetails({ document }: DocumentDetailsProps): ReactNode {
  const { formatMessage } = useIntl();
  const { settings, isDraft, origin, displayLanguage } = usePdfPreviewContext();

  const displayedNumber = useMemo(() => {
    if (origin === ORIGIN.FORM && settings?.numberingMode === 'automatic') {
      return document.number ? document.number : settings.quoteNextNumberFormatted;
    }

    if (isDraft && origin === ORIGIN.MODAL && settings?.numberingMode === 'automatic') {
      return formatMessage({ id: 'pdf-preview.draft.invoice-number-draft-value' });
    }

    return document.number;
  }, [
    origin,
    isDraft,
    document.number,
    settings?.numberingMode,
    settings?.quoteNextNumberFormatted,
    formatMessage,
  ]);

  return (
    <div>
      <NumberDetailsPlaceholder>
        <Details>
          <Detail
            data-testid="pdf-preview-number"
            detailType="number"
            label={formatMessage({ id: 'pdf-preview.quote-number' }, { language: displayLanguage })}
            value={displayedNumber}
          />
        </Details>
      </NumberDetailsPlaceholder>

      <DocumentDetailsPlaceholder>
        <Details>
          <Detail
            data-testid="pdf-preview-issue-date"
            detailType="document"
            label={formatMessage({ id: 'pdf-preview.issue-date' }, { language: displayLanguage })}
            value={<FormattedDate date={document.issueDate} locale={displayLanguage} />}
          />

          <Detail
            data-testid="pdf-preview-due-date"
            detailType="document"
            label={formatMessage(
              { id: 'pdf-preview.quote-expiry-date' },
              { language: displayLanguage }
            )}
            value={<FormattedDate date={document.expiryDate} locale={displayLanguage} />}
          />
        </Details>
      </DocumentDetailsPlaceholder>
    </div>
  );
}

function SelfBillingDetails({ document, relatedInvoice }: DocumentDetailsProps): ReactNode {
  const { formatMessage } = useIntl();
  const { displayLanguage } = usePdfPreviewContext();

  return (
    <div>
      <NumberDetailsPlaceholder>
        <Details>
          <Detail
            data-testid="pdf-preview-number"
            detailType="number"
            label={formatMessage(
              { id: 'pdf-preview.self-billing.self-invoice.number' },
              { language: displayLanguage }
            )}
            value={document.number}
          />
        </Details>
      </NumberDetailsPlaceholder>

      <DocumentDetailsPlaceholder>
        <Details>
          <Detail
            data-testid="pdf-preview-issue-date"
            detailType="document"
            label={formatMessage({ id: 'pdf-preview.issue-date' }, { language: displayLanguage })}
            value={<FormattedDate date={document.issueDate} locale={displayLanguage} />}
          />
          {relatedInvoice?.number ? (
            <Detail
              data-testid="pdf-preview-supplier-invoice-number"
              detailType="document"
              label={formatMessage(
                { id: 'pdf-preview.self-billing.supplier-invoice.number' },
                { language: displayLanguage }
              )}
              value={relatedInvoice.number}
            />
          ) : null}
          {relatedInvoice?.issueDate ? (
            <Detail
              data-testid="pdf-preview-supplier-invoice-issue-date"
              detailType="document"
              label={formatMessage({ id: 'pdf-preview.issue-date' }, { language: displayLanguage })}
              value={<FormattedDate date={relatedInvoice.issueDate} locale={displayLanguage} />}
            />
          ) : null}
        </Details>
      </DocumentDetailsPlaceholder>
    </div>
  );
}

function InvoiceDetails({ document, relatedQuote }: DocumentDetailsProps): ReactNode {
  const { formatMessage } = useIntl();
  const { settings, isDraft, origin, displayLanguage, variant } = usePdfPreviewContext();

  const displayedNumber = useMemo(() => {
    if (origin === ORIGIN.FORM && settings?.numberingMode === 'automatic') {
      return settings.invoiceNextNumberFormatted;
    }

    if (isDraft && origin === ORIGIN.MODAL && settings?.numberingMode === 'automatic') {
      return formatMessage({ id: 'pdf-preview.draft.invoice-number-draft-value' });
    }

    return document.number;
  }, [
    origin,
    isDraft,
    document.number,
    settings?.numberingMode,
    settings?.invoiceNextNumberFormatted,
    formatMessage,
  ]);

  return (
    <div>
      <NumberDetailsPlaceholder>
        <Details>
          <Detail
            data-testid="pdf-preview-number"
            detailType="number"
            label={formatMessage(
              { id: 'pdf-preview.invoice-number' },
              { language: displayLanguage }
            )}
            value={displayedNumber}
          />
        </Details>
      </NumberDetailsPlaceholder>

      <DocumentDetailsPlaceholder>
        <Details>
          <Detail
            data-testid="pdf-preview-issue-date"
            detailType="document"
            label={formatMessage({ id: 'pdf-preview.issue-date' }, { language: displayLanguage })}
            value={<FormattedDate date={document.issueDate} locale={displayLanguage} />}
          />

          {document.performanceStartDate ? (
            <Detail
              data-testid="pdf-preview-performance-date"
              detailType="document"
              label={
                document.performanceEndDate
                  ? formatMessage(
                      { id: 'pdf-preview.performance-period' },
                      { language: displayLanguage }
                    )
                  : formatMessage(
                      { id: 'pdf-preview.performance-date' },
                      { language: displayLanguage }
                    )
              }
              value={
                document.performanceEndDate ? (
                  formatMessage(
                    { id: 'pdf-preview.performance-period-range' },
                    {
                      language: displayLanguage,
                      startDate: (
                        <FormattedDate
                          date={document.performanceStartDate}
                          locale={displayLanguage}
                        />
                      ),
                      endDate: (
                        <FormattedDate
                          date={document.performanceEndDate}
                          locale={displayLanguage}
                        />
                      ),
                    }
                  )
                ) : (
                  <FormattedDate date={document.performanceStartDate} locale={displayLanguage} />
                )
              }
            />
          ) : null}

          {variant !== 'DE' ? (
            <Detail
              data-testid="pdf-preview-due-date"
              detailType="document"
              label={formatMessage({ id: 'pdf-preview.due-date' }, { language: displayLanguage })}
              value={<FormattedDate date={document.dueDate} locale={displayLanguage} />}
            />
          ) : null}

          {document.depositInvoiceNumber ? (
            <Detail
              data-testid="pdf-preview-deposit-invoice-number"
              detailType="document"
              label={formatMessage(
                { id: 'pdf-preview.deposit-invoice-related-number' },
                { language: displayLanguage }
              )}
              value={document.depositInvoiceNumber}
            />
          ) : null}

          {document.depositPaymentDate ? (
            <Detail
              data-testid="pdf-preview-deposit-payment-date"
              detailType="document"
              label={formatMessage(
                { id: 'pdf-preview.deposit-payment-date' },
                { language: displayLanguage }
              )}
              value={<FormattedDate date={document.depositPaymentDate} locale={displayLanguage} />}
            />
          ) : null}

          {document.purchaseOrder ? (
            <Detail
              data-testid="pdf-preview-purchase-order"
              detailType="document"
              label={formatMessage(
                { id: 'pdf-preview.purchase-order' },
                { language: displayLanguage }
              )}
              value={document.purchaseOrder}
            />
          ) : null}

          {relatedQuote?.number ? (
            <Detail
              data-testid="pdf-preview-quote-number"
              detailType="document"
              label={formatMessage(
                { id: 'pdf-preview.quote-related-number' },
                { language: displayLanguage }
              )}
              value={relatedQuote.number}
            />
          ) : null}
        </Details>
      </DocumentDetailsPlaceholder>
    </div>
  );
}
